































import {Vue, Component, Watch} from 'vue-property-decorator';
import {Action, namespace} from "vuex-class";

import { DEFAULT_LANGUAGE, Languages } from '@/constants/locale';
import { OTHER_ROUTES } from './constants/routes';
import { errorHandler } from "@/services/services";
import { isHomeUrl, isLoginRegisterResetOrValidationUrl } from "@/services/routeHelper";
import * as services from '@/services/services';
import {appVersion} from "@/services/env";
import {IAndroidHandlerState} from "@/services/androidKeyboardHandler";

import LanguageSelector from "@/views/components/FormInputs/LanguageSelector.vue";
import LoginRegister from '@/views/components/LoginRegister/LoginRegister.vue';
import DialogSet from "@/views/components/Home/DialogSet.vue";

const userDataModule = namespace("userData");
const systemModule = namespace("system");

@Component({
  name: 'App',
  components: {
    LoginRegister,
    LanguageSelector,
    DialogSet,
  },
})
export default class App extends Vue {
  @Action("initStore") private initStore!: () => Promise<any>;
  @Action("resetStore") private resetStore!: () => Promise<any>;

  @userDataModule.Getter("getLanguage")
  private getLanguage!: Languages;

  @userDataModule.Action("setLanguage")
  private setLanguageVuex!: (lang: Languages) => Promise<any>;

  @userDataModule.Action("refreshUserData")
  private refreshUserData!: () => Promise<any>;

  @userDataModule.Getter("userIsLogged")
  private userIsLogged!: boolean;

  @systemModule.Action("init")
  private initWebappSystem!: () => any;

  private isAppInit: boolean = true;
  private isLoginRegister: boolean = true;
  private unsubscribe: any[] = [];
  private loading: boolean = true;
  private loadingStore: boolean = false;
  private showGenericErrorMessage: boolean = false;
  private showGenericNormalMessage: boolean = false;
  private genericErrorMessage: string = '';
  private genericNormalMessage: string = '';
  private language: Languages = DEFAULT_LANGUAGE;
  private appVersion: string = appVersion;
  private userUpdateIntervalId: number = 0;

  private isAndroid: boolean = false;
  private isAndroidKeyboardOpen: boolean = false;
  private androidState: IAndroidHandlerState = {
    isAndroid: false,
    keyboardIsOpen: false,
    screenIsLandscape: false,
    windowHeight: 0,
    windowWidth: 0,
    currentHeight: 0,
    currentWidth: 0,
  };

  @Watch("language")
  private languageWatcher() {
    // this is login language change: DON'T UPDATE THE BACKEND!!
    if (!this.userIsLogged) {
      this.setLanguageVuex(this.language);
    }
  }

  @Watch("getLanguage")
  private languageVuexWatcher() {
    if (this.language !== this.getLanguage) {
      this.language = this.getLanguage;
    }
  }

  @Watch("$route")
  private routeWatcher() {
      if (isLoginRegisterResetOrValidationUrl(this.$route)) {
        services.authService.clearToken(); // when we enter directly in the login or register urls, we clear the token
      }
  }

  private initLanguage() {
    this.languageWatcher();
    this.languageVuexWatcher();
  }

  private created() {
    this.initWebappSystem(); // init the store so it gets the proper information

    this.$uiBus.on("genericError:show", this.showGenericError);
    this.$uiBus.on("genericError:hide", this.hideGenericError);
    this.$uiBus.on("genericMessage:show", this.showGenericMessage);
    this.$uiBus.on("genericMessage:hide", this.hideGenericMessage);

    this.tokenSubscriber();
    // here we might want to load the info before starting the app
    this.initLanguage();
    this.initAndroidKeyboardFix();
    this.manageToken()
        .finally(() => this.loading = false); // the app finished loading
    this.isAppInit = false;
  }

  private destroyed() {
    this.$uiBus.off("genericError:show", this.showGenericError);
    this.$uiBus.off("genericError:hide", this.hideGenericError);
    this.$uiBus.off("genericMessage:show", this.showGenericMessage);
    this.$uiBus.off("genericMessage:hide", this.hideGenericMessage);
    this.unsubscribe.forEach(each => each.unsubscribe());
  }

  private get getAndroidStyle() {
    const keyboardStyle = `min-height: ${ this.androidState.windowHeight }px; `; /*+
      `min-width:  ${ this.androidState.windowWidth }px;`;*/

    return this.isAndroid && this.isAndroidKeyboardOpen  ? keyboardStyle  : '';
  }

  private initAndroidKeyboardFix() {
    services.androidHandler.isAndroid$$.subscribe((isAndroid: boolean) => this.isAndroid = isAndroid);
    services.androidHandler.keyboardIsOpen$$.subscribe((isOpen: boolean) => this.isAndroidKeyboardOpen = isOpen);
    services.androidHandler.androidState$$
        .subscribe((state: IAndroidHandlerState) => this.androidState = state);
  }

  private clearUpdateUserInterval() {
    if (this.userUpdateIntervalId) {
      clearInterval(this.userUpdateIntervalId);
    }
  }

  private setUpdateUserInterval() {
    this.userUpdateIntervalId = setInterval(() => {
      this.refreshUserData();
    }, 120000);
  }

  private manageToken(): Promise<any>  {
    if (isLoginRegisterResetOrValidationUrl(this.$route)) {
      services.authService.clearToken(); // when we enter directly in the login or register urls, we clear the token
      return Promise.resolve();
    }
    return this.testOldTokenValidity();
  }

  // GENERIC MESSAGES ---------

  private showGenericMessage(message: string) {
    this.showGenericNormalMessage = true;
    this.genericNormalMessage = message;
    setTimeout(this.hideGenericMessage, 4000);
  }

  private hideGenericMessage() {
    this.showGenericNormalMessage = false;
    setTimeout(() => {
      this.genericNormalMessage = '';
    }, 200);
  }

  // GENERIC ERRORS ---------

  private showGenericError(message: string) {
    this.showGenericErrorMessage = true;
    this.genericErrorMessage = message;
    setTimeout(this.hideGenericError, 4000);
  }

  private hideGenericError() {
    this.showGenericErrorMessage = false;
    setTimeout(() => {
      this.genericErrorMessage = '';
    }, 200);
  }


  /**
   * Subscribes to the token stream, if we have a token, we will do all the calls to get info from the server
   * if we clean the token, that is, token === "", we reset the store.
   */
  private tokenSubscriber() {
    // clear store
    this.unsubscribe.push(
      services.authService.whenNoToken$$
        .subscribe( () => {
          this.showLoginPage();
          this.clearUpdateUserInterval();
        }),
    );

    this.unsubscribe.push(
      services.authService.whenExistsToken$$
        .subscribe(() => {
          this.hideLoginRegister();
          this.initSystem();
          this.setUpdateUserInterval();
        }),
    );
  }

  private initSystem(): Promise<any> {
    this.loadingStore = true;
    return this.initStore()
               .catch(this.showErrors)
               .finally(() => this.loadingStore = false);
  }

  private showErrors(err: any) {
    errorHandler.handleErrorsResponse(err, 'initStore.errors');
  }

  private returnToHome() {
    if (!(isHomeUrl(this.$route) || isLoginRegisterResetOrValidationUrl(this.$route))) {
      this.$router.push({name: OTHER_ROUTES.HOME});
    }
  }

  private showLoginPage() {

    // force to return home after loosing token
    this.returnToHome();
    this.loadingStore = true;

    let myPromise: Promise<any> = Promise.resolve();

    if (!this.isAppInit) {
      myPromise = this.resetStore()
                      .then(() => this.isAppInit = false);
    }

    return myPromise
      .then(() => {
        this.showLoginRegister();
      })
      .catch(this.showErrors)
      .finally(() => this.loadingStore = false);
  }

  private showLoginRegister() {
    this.isLoginRegister = true;
  }

  private hideLoginRegister() {
    this.isLoginRegister = false;
  }

  private testOldTokenValidity(): Promise<any> {
    this.loading = true;
    return services.apiCaller.testOldToken()
        .then((res: boolean) => {
          if (res) {
            return this.refreshUserData();
          }
        })
      .finally(() => this.loading = false);
  }
}
